"use client";

import Breadcrumbs from "@/components/projects/Breadcrumbs";
import Explorer from "@/components/projects/Explorer";
import FileTabs from "@/components/projects/FileTabs";
import HeaderIde from "@/components/projects/HeaderIde";
import ProjectCard from "@/components/projects/ProjectCard";
import ProjectShowcase from "@/components/projects/ProjectShowcase";
import SidebarIde from "@/components/projects/SidebarIde";
import type Project from "@/types/project";
import type { ProjectFolder } from "@/types/project";

const FOLDER_SECTIONS: { id: ProjectFolder; label: string }[] = [
    { id: "favorites", label: "Favoris" },
    { id: "inProgress", label: "En cours" },
    { id: "archived", label: "Archive" },
];
import { useCallback, useEffect, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import Link from "next/link";
import { ArrowLeft } from "lucide-react";

export default function Ide({ projects, initialProject }: { projects: Project[]; initialProject: Project | null; }) {
    const router = useRouter();
    const pathname = usePathname();
    const searchParams = useSearchParams();
    const [explorerOpen, setExplorerOpen] = useState(true);
    const [activeTab, setActiveTab] = useState<string>(initialProject ? "file" : "search");
    const [activeProject, setActiveProject] = useState<string>(initialProject?.id ?? "");
    const [openProjects, setOpenProjects] = useState<string[]>(
        initialProject ? [initialProject.id] : []
    );

    const handleExplorerOpen = () => {
        setExplorerOpen(!explorerOpen);
    };

    const getProject = useCallback(
        (id: string) => projects.find((project) => project.id === id) ?? projects[0],
        [projects]
    );

    const setUrlFromProject = useCallback(
        (projectId: string) => {
            const project = projects.find((item) => item.id === projectId);
            if (!project) return;
            router.push(`${pathname}?project=${project.anchor}`, { scroll: false });
        },
        [pathname, projects, router]
    );

    const clearUrl = useCallback(() => {
        router.push(pathname, { scroll: false });
    }, [pathname, router]);

    useEffect(() => {
        const slug = searchParams.get("project");
        if (!slug) {
            setActiveTab("search");
            return;
        }

        const projectFromQuery = projects.find((project) => project.anchor === slug);
        if (!projectFromQuery) {
            setActiveTab("search");
            return;
        }

        setActiveProject(projectFromQuery.id);
        setActiveTab("file");
        setOpenProjects((current) =>
            current.includes(projectFromQuery.id) ? current : [...current, projectFromQuery.id]
        );
    }, [projects, searchParams]);

    const breadcrumbItems = activeTab === "file"
        ? [
            { label: "src", onClick: () => { clearUrl(); setActiveTab("search"); } },
            { label: "projects", onClick: () => { clearUrl(); setActiveTab("search"); } },
            {
                label: getProject(activeProject).fileName,
                onClick: () => setUrlFromProject(activeProject),
            },
        ]
        : [
            { label: "src", onClick: () => { clearUrl(); setActiveTab("search"); } },
            { label: "projets" },
        ];

    const handleOpenProject = (projectId: string) => {
        setActiveProject(projectId);
        setActiveTab("file");
        setOpenProjects((current) => (current.includes(projectId) ? current : [...current, projectId]));
        setUrlFromProject(projectId);
    };

    const handleCloseProject = (projectId: string) => {
        setOpenProjects((current) => {
            const updatedProjects = current.filter((id) => id !== projectId);
            if (updatedProjects.length === 0) {
                setActiveTab("search");
                clearUrl();
                return updatedProjects;
            }

            if (activeProject === projectId) {
                const nextProjectId = updatedProjects[updatedProjects.length - 1];
                setActiveProject(nextProjectId);
                setUrlFromProject(nextProjectId);
            }

            return updatedProjects;
        });
    };

    return (
        <main className="pt-20 lg:pt-0 lg:pl-20 flex flex-col h-dvh overflow-hidden">
            <HeaderIde
                projects={projects}
                onProjectSelect={handleOpenProject}
                toggleExplorer={handleExplorerOpen}
            />
            <div className="flex flex-1 min-h-0 overflow-hidden w-full">
                <SidebarIde
                    toggleExplorer={handleExplorerOpen}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    clearUrl={clearUrl}
                />
                <Explorer
                    projects={projects}
                    explorerOpen={explorerOpen}
                    activeProject={activeProject}
                    onProjectSelect={handleOpenProject}
                />

                <div className="flex-1 min-w-0 flex flex-col bg-black overflow-hidden">
                    <FileTabs
                        projects={projects}
                        openProjectIds={openProjects}
                        activeProject={activeProject}
                        activeTab={activeTab}
                        handleOpenProject={handleOpenProject}
                        handleCloseProject={handleCloseProject}
                    />

                    <div className="flex-1 min-h-0 p-4 lg:p-6 overflow-y-auto overflow-x-hidden">
                        {activeTab === "file" && activeProject ? (
                            <ProjectShowcase project={getProject(activeProject)} />
                        ) : (
                            <div className="space-y-10">
                                <Breadcrumbs items={breadcrumbItems} className="mb-4 lg:mb-6" />
                                {FOLDER_SECTIONS.map(({ id, label }) => {
                                    const folderProjects = projects.filter((p) => p.folder === id);
                                    if (folderProjects.length === 0) return null;
                                    return (
                                        <section key={id} aria-labelledby={`ide-folder-${id}`}>
                                            <h2
                                                id={`ide-folder-${id}`}
                                                className="text-[10px] font-mono uppercase tracking-widest text-on-surface-variant mb-4"
                                            >
                                                {label}
                                            </h2>
                                            <div className="grid md:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4 gap-6">
                                                {folderProjects.map((project) => (
                                                    <ProjectCard
                                                        key={project.id}
                                                        project={project}
                                                        onOpenProject={handleOpenProject}
                                                    />
                                                ))}
                                            </div>
                                        </section>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
}
